import React, { useMemo, useEffect, useState } from "react";
import { Button } from "@mui/material";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import { useApiClientQuery } from "../../../hooks/useApiClient";
import api_endpoints from "../../../api";
import PredefinedPeriods from "../../../widgets/CustomDatePicker/PredefinedPeriod";
import { currencyFormatter, dateTimeReadableSimple, formatDateTime } from "../../../utils";
import { useCustomTranslation } from "../../../hooks/useCustomTranslation";
import ModeHeader from "./ModeHeader";
import PromisesForCampaignTable from "./PromisesForCampaignTable";
import TrendChart from "./TrendChart";
import RightStatsPanel from "./RightStatsPanel";
import { getPageFromName } from "../../../route_utils";

const asArray = (v) => {
  if (Array.isArray(v)) return v;
  if (v && Array.isArray(v.data)) return v.data;
  if (v && Array.isArray(v.transactions)) return v.transactions;
  return [];
};

const CampaignMode = ({
  organizationId,
  campaign,
  period,
  setPeriod,
  onBack,
  onAddPromise,
  refreshKey,
}) => {
  const { t } = useCustomTranslation("Promises List");
  const navigate = useNavigate();

  const handlePeriodChange = (e) =>
    setPeriod((p) => ({ ...p, selectedPeriod: e.target.value }));

  const setpersonalizedDates = (startDate, endDate, starttime, endtime) =>
    setPeriod((p) => ({
      ...p,
      start_date: startDate,
      end_date: endDate,
      start_time: starttime.format("HH:mm"),
      end_time: endtime.format("HH:mm"),
    }));

  const { data: rawData, isFetching } = useApiClientQuery(
    () =>
      api_endpoints.getPromises(
        organizationId,
        campaign.name, // campaign filter
        null, null, null, // donor/email/phone
        null, null, null, null, // promise_amount/status/payment_type/promise_type
        null,
        formatDateTime(period.start_date, period.start_time),
        formatDateTime(period.end_date, period.end_time)
      ),
    { queryKey: ["campaign_promises", organizationId, campaign.name, period, refreshKey] }
  );

  const data = useMemo(() => asArray(rawData), [rawData]);

  const listRows = useMemo(
    () =>
      data.map((p) => ({
        id: p._id,
        email: p.donor_profile?.email,
        name: `${p.donor_profile?.first_name || ""} ${p.donor_profile?.last_name || ""}`.trim(),
        phone: p.donor_profile?.phone,
        created_at: dateTimeReadableSimple(p.created_at),
        created_at_value: p.created_at,
        payment_type: t(p.payment_type),
        payment_type_value: p.payment_type,
        promise_type: t(p.promise_type),
        promise_type_value: p.promise_type,
        given_amount: currencyFormatter(Number(p.given_amount || 0).toFixed(2)),
        given_amount_value: Number(p.given_amount || 0),
        promise_amount: currencyFormatter(Number(p.promise_amount || 0).toFixed(2)),
        promise_amount_value: Number(p.promise_amount || 0),
        payment_frequency: p.payment_frequency ? t(p.payment_frequency) : "-",
        payment_frequency_value: p.payment_frequency || "-",
        end_date: p.end_date ? dateTimeReadableSimple(p.end_date) : "-",
        end_date_value: p.end_date,
        status: p.status,
      })),
    [data, t]
  );

  const totals = useMemo(() => {
    const promised = data.reduce((s, p) => s + Number(p.promise_amount || 0), 0);
    const given = data.reduce((s, p) => s + Number(p.given_amount || 0), 0);
    return { promised, given, uncollected: Math.max(0, promised - given) };
  }, [data]);

  const rightStats = [
    { label: t("transactions"), value: listRows.length },
    { label: t("promises"), value: currencyFormatter(totals.promised.toFixed(2)) },
    { label: t("donations_received"), value: currencyFormatter(totals.given.toFixed(2)) },
    { label: t("uncollected_promises"), value: currencyFormatter(totals.uncollected.toFixed(2)) },
  ];

  // chart data for this campaign – fully defensive
  const [chartData, setChartData] = useState([]);

  useEffect(() => {
    let cancelled = false;

    const build = async () => {
      const list = asArray(data);
      if (!list.length) { setChartData([]); return; }

      const promisedByDay = {};
      list.forEach((p) => {
        const d = p?.created_at ? dayjs(p.created_at).format("YYYY-MM-DD") : null;
        if (!d) return;
        promisedByDay[d] = (promisedByDay[d] || 0) + Number(p.promise_amount || 0);
      });

      const actualByDay = {};
      const txResponses = await Promise.all(
        list.map((p) =>
          api_endpoints.getCampaignPromiseTransactions(p._id, null, null, null, null)
        )
      );

      txResponses.forEach((txListRaw) => {
        const txList = asArray(txListRaw);
        txList.forEach((tx) => {
          const raw =
            tx?.paymentDetails?.date_of_donation ||
            tx?.paymentDetails?.date_of_transfer;
          if (!raw) return;
          const d = dayjs(raw).format("YYYY-MM-DD");
          actualByDay[d] = (actualByDay[d] || 0) + Number(tx.amount || 0);
        });
      });

      const start = dayjs(period.start_date).startOf("day");
      const end = dayjs(period.end_date).startOf("day");
      let accP = 0, accA = 0;
      const points = [];
      for (let cur = start; !cur.isAfter(end); cur = cur.add(1, "day")) {
        const key = cur.format("YYYY-MM-DD");
        accP += promisedByDay[key] || 0;
        accA += actualByDay[key] || 0;
        points.push({ date: key, promised: accP, actual: accA });
      }

      if (!cancelled) setChartData(points);
    };

    build();
    return () => { cancelled = true; };
  }, [data, period.start_date, period.end_date]);

  const openPromiseDetails = (e) => {
    navigate(getPageFromName("Promesse").path, { state: { promise_id: e.id } });
  };

  return (
    <>
      <ModeHeader
        title={t("promises_for_campaign", { campaign: campaign.name })}
        onBack={onBack}
        right={<Button variant="contained" onClick={onAddPromise}>{t("add_promise_button")}</Button>}
      />

      <div className="container-white">
        <div className="inline-flex w-fit items-center">
        <PredefinedPeriods
          selectedPeriod={period.selectedPeriod}
          handlePeriodChange={handlePeriodChange}
          setSelectedPeriod={(v) => setPeriod((p) => ({ ...p, selectedPeriod: v }))}
          setpersonalizedDates={setpersonalizedDates}
        />
        </div>
      </div>

      <div className="flex gap-4">
        <div className="flex-1">
          <TrendChart
            data={chartData}
            labels={{ promised: t("promised"), actual: t("actual_donations") }}
          />
        </div>
        <RightStatsPanel title="" stats={rightStats} />
      </div>

      <PromisesForCampaignTable
        rows={listRows}
        loading={isFetching}
        onOpenDetails={openPromiseDetails}
      />
    </>
  );
};

export default CampaignMode;
